'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useFoundry } from '@/lib/foundry/context';
import type { BusinessPlan, PlanStatus, LeanCanvas } from '@/lib/foundry/types';

const STATUS_COLORS: Record<PlanStatus, string> = {
  draft: 'bg-yellow-500/20 text-yellow-300',
  canvas_complete: 'bg-blue-500/20 text-blue-300',
  in_progress: 'bg-accent-muted text-accent',
  completed: 'bg-green-500/20 text-green-300',
  archived: 'bg-gray-500/20 text-gray-300',
};

const STATUS_LABELS: Record<PlanStatus, string> = {
  draft: 'Draft',
  canvas_complete: 'Canvas Complete',
  in_progress: 'In Progress',
  completed: 'Completed',
  archived: 'Archived',
};

// Calculate canvas completeness (0-9 sections)
function getCanvasCompleteness(canvas: LeanCanvas | null): number {
  if (!canvas) return 0;
  const sections = [
    canvas.problem,
    canvas.customer_segments,
    canvas.unique_value_proposition,
    canvas.solution,
    canvas.channels,
    canvas.revenue_streams,
    canvas.cost_structure,
    canvas.key_metrics,
    canvas.unfair_advantage,
  ];
  return sections.filter(Boolean).length;
}

// Get viability indicator based on canvas data
function getViabilityInfo(canvas: LeanCanvas | null): {
  score: number; // 0-100
  label: string;
  color: string;
} {
  if (!canvas) {
    return { score: 0, label: 'Not Started', color: 'text-foreground-muted' };
  }

  const completeness = getCanvasCompleteness(canvas);
  const defensibility = canvas.unfair_advantage?.defensibility_score ?? 0;

  // Calculate weighted score: 60% completeness + 40% defensibility
  const completenessScore = (completeness / 9) * 60;
  const defensibilityScore = (defensibility / 10) * 40;
  const score = Math.round(completenessScore + defensibilityScore);

  if (completeness === 0) {
    return { score: 0, label: 'Not Started', color: 'text-foreground-muted' };
  }
  if (score >= 80) {
    return { score, label: 'Strong', color: 'text-green-400' };
  }
  if (score >= 60) {
    return { score, label: 'Promising', color: 'text-blue-400' };
  }
  if (score >= 40) {
    return { score, label: 'Developing', color: 'text-yellow-400' };
  }
  return { score, label: 'Early Stage', color: 'text-accent' };
}

function PlanCard({ plan }: { plan: BusinessPlan }) {
  const { actions } = useFoundry();
  const viability = getViabilityInfo(plan.lean_canvas);
  const canvasCompleteness = getCanvasCompleteness(plan.lean_canvas);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (confirm(`Are you sure you want to delete "${plan.name}"?`)) {
      await actions.deletePlan(plan.id);
    }
  };

  return (
    <Link
      href={`/tools/foundry/editor?id=${plan.id}`}
      className="block p-6 bg-background/40 border border-border-muted rounded-lg hover:border-accent/40 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-lg font-semibold text-foreground group-hover:text-accent transition-colors">
          {plan.name}
        </h3>
        <span
          className={`px-2 py-1 text-xs font-medium rounded ${STATUS_COLORS[plan.status]}`}
        >
          {STATUS_LABELS[plan.status]}
        </span>
      </div>

      {plan.description && (
        <p className="text-foreground-tertiary text-sm mb-3 line-clamp-2">
          {plan.description}
        </p>
      )}

      {/* Viability Indicator */}
      {canvasCompleteness > 0 && (
        <div className="mb-3 p-3 bg-background/60 rounded-lg border border-border-muted">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-tertiary">Viability</span>
            <span className={`text-sm font-semibold ${viability.color}`}>
              {viability.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Score bar */}
            <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  viability.score >= 80
                    ? 'bg-green-500'
                    : viability.score >= 60
                    ? 'bg-blue-500'
                    : viability.score >= 40
                    ? 'bg-yellow-500'
                    : 'bg-accent'
                }`}
                style={{ width: `${viability.score}%` }}
              />
            </div>
            <span className="text-xs text-foreground-muted w-8 text-right">
              {viability.score}%
            </span>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-foreground-muted">
            <span>{canvasCompleteness}/9 canvas sections</span>
            {plan.lean_canvas?.unfair_advantage && (
              <span>
                Moat: {plan.lean_canvas.unfair_advantage.defensibility_score}/10
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-foreground-muted">
        <span>
          {plan.industry ? plan.industry.replace('_', ' ') : 'No industry'}
        </span>
        <span>
          Updated {new Date(plan.updated_at).toLocaleDateString()}
        </span>
      </div>

      <button
        onClick={handleDelete}
        className="mt-4 text-xs text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        Delete plan
      </button>
    </Link>
  );
}

export function PlanList() {
  const { state, actions } = useFoundry();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once per component mount
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    actions.loadPlans();
  }, [actions]);

  if (state.plansLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
        {state.error}
      </div>
    );
  }

  if (state.plans.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-foreground-tertiary mb-4">No business plans yet</p>
        <Link
          href="/tools/foundry/new"
          className="inline-block px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
        >
          Create Your First Plan
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {state.plans.map((plan) => (
        <PlanCard key={plan.id} plan={plan} />
      ))}
    </div>
  );
}
